import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Excalidraw,
  exportToBlob,
  exportToSvg,
  serializeAsJSON,
} from '@excalidraw/excalidraw';
import '@excalidraw/excalidraw/index.css';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Save,
  Image as ImageIcon,
  FileCode2,
  FolderOpen,
  Trash2,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// AdminStudio — hidden utility for sketching & concepting.
// Phase 1: Excalidraw 2D canvas with PNG/SVG export and DB-backed gallery.
// ---------------------------------------------------------------------------
const AdminStudio = () => {
  const navigate = useNavigate();
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [sketches, setSketches] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [title, setTitle] = useState('Untitled sketch');
  const [saving, setSaving] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSketches = useCallback(async () => {
    setGalleryLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/studio/sketches`, {
        headers: authHeaders(),
      });
      setSketches(res.data || []);
    } catch (err) {
      toast.error('Could not load gallery');
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSketches();
  }, [fetchSketches]);

  // ---- Helpers --------------------------------------------------------------
  const getSceneState = () => {
    if (!excalidrawAPI) return null;
    return {
      elements: excalidrawAPI.getSceneElements(),
      appState: excalidrawAPI.getAppState(),
      files: excalidrawAPI.getFiles(),
    };
  };

  const blobToDataUrl = (blob) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });

  const makeThumbnail = async () => {
    if (!excalidrawAPI) return null;
    const state = getSceneState();
    if (!state || state.elements.length === 0) return null;
    try {
      const blob = await exportToBlob({
        elements: state.elements,
        appState: { ...state.appState, exportBackground: true, exportPadding: 24 },
        files: state.files,
        mimeType: 'image/png',
        getDimensions: () => ({ width: 600, height: 400 }),
      });
      return await blobToDataUrl(blob);
    } catch (err) {
      return null;
    }
  };

  // ---- Save -----------------------------------------------------------------
  const handleSave = async () => {
    if (!excalidrawAPI) return;
    setSaving(true);
    try {
      const state = getSceneState();
      // serializeAsJSON returns a string; parse it back so the backend stores JSON
      const snapshotJson = serializeAsJSON(state.elements, state.appState, state.files, 'local');
      const snapshot = JSON.parse(snapshotJson);
      const thumbnail = await makeThumbnail();
      const body = {
        title: title || 'Untitled',
        kind: 'sketch_2d',
        snapshot,
        thumbnail_data_url: thumbnail,
      };
      if (activeId) {
        await axios.put(
          `${BACKEND_URL}/api/admin/studio/sketches/${activeId}`,
          body,
          { headers: authHeaders() },
        );
        toast.success('Sketch updated');
      } else {
        const res = await axios.post(
          `${BACKEND_URL}/api/admin/studio/sketches`,
          body,
          { headers: authHeaders() },
        );
        setActiveId(res.data.id);
        toast.success('Sketch saved');
      }
      await fetchSketches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---- Open from gallery ----------------------------------------------------
  const handleOpen = async (id) => {
    if (!excalidrawAPI) return;
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/studio/sketches/${id}`,
        { headers: authHeaders() },
      );
      const doc = res.data;
      const snap = doc?.snapshot || {};
      excalidrawAPI.updateScene({
        elements: snap.elements || [],
        appState: snap.appState || {},
      });
      if (snap.files) {
        excalidrawAPI.addFiles(Object.values(snap.files));
      }
      setActiveId(doc.id);
      setTitle(doc.title || 'Untitled');
      toast.success(`Opened: ${doc.title}`);
    } catch (err) {
      toast.error('Could not open sketch');
    }
  };

  // ---- Delete ---------------------------------------------------------------
  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/studio/sketches/${confirmDelete.id}`,
        { headers: authHeaders() },
      );
      toast.success(`Deleted: ${confirmDelete.title}`);
      if (activeId === confirmDelete.id) handleNew();
      setConfirmDelete(null);
      await fetchSketches();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // ---- New blank ------------------------------------------------------------
  const handleNew = () => {
    if (!excalidrawAPI) return;
    excalidrawAPI.resetScene();
    setActiveId(null);
    setTitle('Untitled sketch');
  };

  // ---- Export PNG -----------------------------------------------------------
  const handleExportPNG = async () => {
    if (!excalidrawAPI) return;
    try {
      const state = getSceneState();
      if (!state || state.elements.length === 0) {
        toast.error('Canvas is empty');
        return;
      }
      const blob = await exportToBlob({
        elements: state.elements,
        appState: { ...state.appState, exportBackground: true, exportPadding: 32 },
        files: state.files,
        mimeType: 'image/png',
        quality: 1,
      });
      downloadBlob(blob, `${safeFilename(title)}.png`);
      toast.success('PNG exported');
    } catch (err) {
      toast.error('PNG export failed');
    }
  };

  // ---- Export SVG -----------------------------------------------------------
  const handleExportSVG = async () => {
    if (!excalidrawAPI) return;
    try {
      const state = getSceneState();
      if (!state || state.elements.length === 0) {
        toast.error('Canvas is empty');
        return;
      }
      const svg = await exportToSvg({
        elements: state.elements,
        appState: { ...state.appState, exportBackground: true, exportPadding: 32 },
        files: state.files,
      });
      const svgText = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([svgText], { type: 'image/svg+xml' });
      downloadBlob(blob, `${safeFilename(title)}.svg`);
      toast.success('SVG exported');
    } catch (err) {
      toast.error('SVG export failed');
    }
  };

  return (
    <AdminLayout>
      <div
        className="-m-8 flex flex-col"
        style={{ height: 'calc(100vh - 4rem)' }}
        data-testid="admin-studio-page"
      >
        {/* Toolbar */}
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm">
          <h1 className="text-lg font-bold mr-2" style={{ color: '#00CED1' }}>Studio</h1>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Sketch title"
            className="max-w-xs"
            data-testid="admin-studio-title"
          />
          <Button
            onClick={handleSave}
            disabled={saving}
            className="text-white font-semibold"
            style={{ backgroundColor: '#00CED1' }}
            data-testid="admin-studio-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {activeId ? 'Update' : 'Save'}
          </Button>
          <Button variant="outline" onClick={handleNew} data-testid="admin-studio-new">
            <Plus className="h-4 w-4 mr-2" />
            New
          </Button>
          <div className="h-6 w-px bg-gray-200 mx-1" />
          <Button variant="outline" onClick={handleExportPNG} data-testid="admin-studio-export-png">
            <ImageIcon className="h-4 w-4 mr-2" />
            Export PNG
          </Button>
          <Button variant="outline" onClick={handleExportSVG} data-testid="admin-studio-export-svg">
            <FileCode2 className="h-4 w-4 mr-2" />
            Export SVG
          </Button>
          <div className="flex-grow" />
          <Button
            variant="outline"
            onClick={() => setGalleryOpen((g) => !g)}
            data-testid="admin-studio-toggle-gallery"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            {galleryOpen ? 'Hide gallery' : `Gallery (${sketches.length})`}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/dashboard')}
            data-testid="admin-studio-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Canvas + Gallery */}
        <div className="flex-grow flex overflow-hidden">
          <div className="flex-grow relative" data-testid="admin-studio-canvas">
            <Excalidraw
              excalidrawAPI={(api) => setExcalidrawAPI(api)}
              initialData={{ appState: { viewBackgroundColor: '#FAFAFA' } }}
            />
          </div>
          {galleryOpen && (
            <aside
              className="w-72 bg-white border-l overflow-y-auto p-3 space-y-2"
              data-testid="admin-studio-gallery"
            >
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
                Saved sketches
              </p>
              {galleryLoading && (
                <p className="text-sm text-gray-500">Loading…</p>
              )}
              {!galleryLoading && sketches.length === 0 && (
                <p className="text-sm text-gray-500 px-1">No saved sketches yet.</p>
              )}
              {sketches.map((s) => (
                <GalleryCard
                  key={s.id}
                  sketch={s}
                  isActive={s.id === activeId}
                  onOpen={() => handleOpen(s.id)}
                  onDelete={() => setConfirmDelete(s)}
                />
              ))}
            </aside>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete sketch?"
        description={
          confirmDelete
            ? `"${confirmDelete.title}" will be permanently deleted. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        testId="admin-studio-confirm-delete"
      />
    </AdminLayout>
  );
};

const GalleryCard = ({ sketch, isActive, onOpen, onDelete }) => (
  <div
    className={`border rounded-lg overflow-hidden hover:border-teal-400 transition-colors ${isActive ? 'border-teal-500 ring-2 ring-teal-200' : 'border-gray-200'}`}
    data-testid={`admin-studio-gallery-card-${sketch.id}`}
  >
    <button
      type="button"
      onClick={onOpen}
      className="block w-full text-left"
      data-testid={`admin-studio-open-${sketch.id}`}
    >
      <div className="aspect-video bg-gray-50 flex items-center justify-center overflow-hidden">
        {sketch.thumbnail_url ? (
          <img
            src={sketch.thumbnail_url}
            alt={sketch.title}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : (
          <ImageIcon className="h-8 w-8 text-gray-300" />
        )}
      </div>
      <div className="p-2">
        <p className="text-sm font-semibold text-gray-800 truncate">{sketch.title}</p>
        <p className="text-[10px] text-gray-500 mt-0.5">
          {sketch.owner} · {sketch.updated_at?.slice(0, 10)}
        </p>
      </div>
    </button>
    <div className="px-2 pb-2 flex justify-end">
      <button
        type="button"
        onClick={onDelete}
        className="text-gray-400 hover:text-red-600 p-1"
        data-testid={`admin-studio-delete-${sketch.id}`}
        title="Delete sketch"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);

const safeFilename = (s) =>
  (s || 'sketch').replace(/[^a-z0-9_\-]+/gi, '_').slice(0, 60) || 'sketch';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default AdminStudio;
